<template>
  <div class="read-aloud-container">
    <el-row>
      <el-col :span="isGeneralAdmin || hasFilter ? 12 : 24">
        <organization-breadcrumb :items="[$t('study_sets.curriculum'), $t('read_aloud.title')]" />
      </el-col>
      <el-col v-if="isGeneralAdmin || hasFilter" :span="12">
        <admin-organization-switcher />
      </el-col>
    </el-row>
    <div class="read-aloud-tab-body">
      <curriculum-menu :active-entry="`/organization/${organizationId}/read-aloud`" />
      <template>
        <div class="no-read-aloud" v-if="!loading && !(readAlouds && readAlouds.length !== 0) && queryString == null">
          <curriculum-image />
          <h2 style="font-weight: 600; font-size: 32px; text-align: center">
            {{ $t("read_aloud.no_read_aloud_yet") }}
          </h2>
          <p style="font-size: 16px">
            {{ $t("read_aloud.no_read_aloud_desc") }}
          </p>
          <div class="create-new-read-aloud">
            <router-link :to="'/organization/' + organizationId + '/curriculum/read-aloud/new'"
              v-if="isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher" tag="button">
              {{ $t("read_aloud.create_read_aloud") }}
            </router-link>
          </div>
        </div>
        <div v-else class="elsa-table-layout-header">
          <div class="header-end">
            <div class="more-action-text">
              <label class="mr-px-16">
                {{ $t("homework.assignment_lib.table_selected_label", [selectedReadAlouds.length, readAloudsCount]) }}
              </label>
              <a @click="selectAll(paginateReadAlouds.results)">
                {{ selectText }}
              </a>
            </div>
          </div>
          <div class="header-end">
            <div>
              <el-form class="search-form">
                <el-tooltip :visible-arrow="false" :content="$t('study_sets.table.search_hint')" placement="bottom-start">
                  <elsa-search v-model="queryString" @search="searchReadAloud"
                    :placeholder="$t('read_aloud.search_read_aloud')" />
                </el-tooltip>
              </el-form>
            </div>
            <div class="create-new-read-aloud">
              <router-link :to="'/organization/' + organizationId + '/curriculum/read-aloud/new'"
                v-if="isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher" tag="button">
                <span>
                  {{ $t("read_aloud.create_read_aloud") }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="remove-btn-div" v-if="selectedReadAlouds && selectedReadAlouds.length > 0">
      <el-button slot="reference" @click="changeActions('remove')" class="remove-btn">
        <font-awesome-icon :icon="['fas', 'trash-alt']" style="color: #eb5757; font-size: 14px; line-height: 14px" />
      </el-button>
    </div>
    <base-table-loading v-if="loading" :data="skeletonData" />
    <template v-else>
      <table-layout v-if="(readAlouds && readAlouds.length !== 0) || queryString != null" :showTitle="false"
        :pager-counter="pageCounter" :pageSize="numberElementsPerPage" :total="readAloudsCount"
        :currentChange="getReadAloudPage" :current-page.sync="currentPage" :query-placeholder="$t('common.search')"
        :title="$t('study_sets.curriculum')" v-loading="loading">
        <div class="table-wrapper">
          <el-table ref="multipleTable" @sort-change="sortChange" :data="readAlouds" :empty-text="$t('common.no_values')"
            @select-all="handleSelectRow" @select="handleSelectRow"
            :default-sort="{ prop: 'updated_at', order: 'descending' }">
            <el-table-column type="selection" width="40"> </el-table-column>
            <el-table-column sortable="custom" prop="name" :label="$t('study_sets.table.study_set_name')"
              :sort-orders="['descending', 'ascending']" min-width="150">
              <template slot-scope="scope">
                <router-link :to="'/organization/' + organizationId + '/curriculum/read-aloud/' + scope.row.id">
                  <span style="font-size: 16px">{{ scope.row.name }}</span>
                </router-link>
              </template>
            </el-table-column>
            <el-table-column dataType="date" sort-by="updated_at" prop="updated_at" sortable="custom"
              :sort-orders="['descending', 'ascending']" :label="$t('study_sets.table.last_updated')" align="center"
              width="150">
              <template slot-scope="scope">
                {{ formatDate(scope.row.updated_at) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t($i18nLabelsHelper(currentOrganizationType, `study_sets.recent_assigned_group`))"
              min-width="150">
              <template slot-scope="scope">
                {{ getRecentAssignGroupName(scope.row) }}
              </template>
            </el-table-column>
            <!-- <el-table-column
              prop="category"
              :label="$t('study_sets.table.category')"
              width="120"
              :filters="categoryOptions"
              :filter-method="filterCategory"
              filter-placement="bottom-end"
            >
              <template slot-scope="scope">
                {{ getCategoryLabel(scope.row.category) }}
              </template>
            </el-table-column> -->
            <el-table-column prop="items" :label="$t('read_aloud.table.word_length')" align="center">
              <template slot-scope="scope">
                {{ scope.row.passage.trim().split(/\s+/).length }}
              </template>
            </el-table-column>
            <el-table-column prop="shareable" :label="$t('study_sets.table.shareable_to')" width="140" :filters="[
              {
                text: $t($i18nLabelsHelper(currentOrganizationType, `study_sets.group`)),
                value: 'group',
              },
              {
                text: $t($i18nLabelsHelper(currentOrganizationType, `study_sets.organization`)),
                value: 'organization',
              },
              {
                text: $t($i18nLabelsHelper(currentOrganizationType, `study_sets.public`)),
                value: 'public',
              },
            ]" :filter-method="filterShareable" filter-placement="bottom-end">
              <template slot-scope="scope">
                {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.${scope.row.shareable}`)) }}
              </template>
            </el-table-column>
            <el-table-column v-if="this.isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher"
              align="right" width="220">
              <template slot-scope="scope">
                <div class="flex" style="justify-content: flex-end">
                  <span class="assign-to-class" @click="handleAssignToClassReadAloud(scope.row)">
                    <chip>
                      {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_group`)) }}
                    </chip>
                  </span>
                  <el-popover placement="bottom-end" trigger="click" :width="130" :visible-arrow="false"
                    style="z-index: 1903">
                    <div class="actions-list">
                      <span @click="handleEditReadAloud(scope.row.id)"
                        :class="{ 'btn-edit-disabled': canAccessReadAloud(scope.row.id, 'view') }">
                        <span style="width: 24px; height: 24px; padding-right: 8px">
                          <font-awesome-icon icon="pen" />
                        </span>
                        {{ $t("study_sets.table.edit") }}
                      </span>
                      <span v-show="false" @click="handleDeleteReadAloud(scope.row.id)" class="remove-read-aloud">
                        <span style="width: 24px; height: 24px; padding-right: 8px">
                          <font-awesome-icon :icon="['fas', 'trash-alt']"
                            style="color: #eb5757; font-size: 14px; line-height: 14px;" />
                        </span>
                        {{ $t("study_sets.table.remove") }}
                      </span>
                    </div>
                    <el-tooltip :visible-arrow="false" class="item" content="Options" placement="top" slot="reference">
                      <el-button style="padding: 13px 16px; border-radius: 8px">
                        <i class="el-icon-more"></i>
                      </el-button>
                    </el-tooltip>
                  </el-popover>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </table-layout>
    </template>
    <add-assignment-wrapper ref="addAssignment" v-on:reload="handleReload" />
    <confirm-delete-read-aloud-modal :dialogVisible="modals.showConfirmDelete" append-to-body
      v-on:closed="modals.showConfirmDelete = false" v-on:delete="handleConfirmDeleteReadAloud">
      <template v-slot:body>
        <h4 id="leave-modal-title">
          {{ $t("read_aloud.modals_delete_title") }}
        </h4>
      </template>
    </confirm-delete-read-aloud-modal>
  </div>
</template>

<script>
import curriculumImage from "@/assets/images/inline.curriculum-image.svg";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import { format } from "date-fns";
import { mapGetters, mapActions } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import TableLayout from "@/components/studysets/TableLayout";
import { always, pathOr } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from "@/constants/generic";
import Chip from "@/components/common/Chip";
import AddAssignmentWrapper from "@/components/modals/read-aloud/AddAssignmentWrapper";
import ElsaSearch from "@/components/studysets/Search";
import { DefaultShareableOptions, DefaultCategoryOptions } from "@/constants/studySets";
import { getOptionsLabels } from "@/utils/elementUi";
import ConfirmDeleteReadAloudModal from "@/components/modals/bases/ConfirmDelete.vue";
import CurriculumMenu from "@/components/studysets/CurriculumMenu";
import { DATE_FORMAT } from "@/constants/generic";
import AdminOrganizationSwitcher from "@/components/dashboard/AdminOrganizationSwitcher";
import BaseTableLoading from "@/components/common/BaseTableLoading.vue";

export default {
  components: {
    BaseTableLoading,
    curriculumImage,
    OrganizationBreadcrumb,
    TableLayout,
    Chip,
    ElsaSearch,
    AddAssignmentWrapper,
    ConfirmDeleteReadAloudModal,
    CurriculumMenu,
    AdminOrganizationSwitcher,
  },
  props: {
    dateFormat: {
      type: String,
      default: DATE_FORMAT,
    },
  },
  data() {
    return {
      modals: {
        showConfirmDelete: false,
      },
      deleteReadAloudIds: [],
      selectedReadAloudToAssign: null,
      activeEntry: "1",
      currentPage: 1,
      queryString: null,
      ordering: "-updated_at",
      loading: true,
      selectedReadAlouds: [],
      selectedReadAloudsCount: 0,
      paginateReadAlouds: {
        results: [],
        count: 0,
      },
      selectText: this.$t("common.select_all"),
      categoryOptions: [],
      checkedReadAlouds: {
        view: [],
        edit: [],
      },
      skeletonData: [
        {
          name: "Class Name",
          label: "Name",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Last Updated",
          label: "Last Updated",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Recent assigned class",
          label: "Recent assigned class",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Category",
          label: "Category",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Items",
          label: "Items",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Share To",
          label: "Share To",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "100%",
                  height: "12px",
                },
              ],
            },
            {
              width: "70%",
              flexDirection: "row",
              items: [
                {
                  type: "rectangle",
                  width: "80%",
                  height: "12px",
                },
                {
                  width: "20%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
      ],
    };
  },
  computed: {
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    readAloudsCount() {
      return pathOr(0, ["paginateReadAlouds", "count"])(this);
    },
    readAlouds() {
      return pathOr([], ["paginateReadAlouds", "results"])(this);
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    ...mapGetters("organization", [
      "isOrganizationAdmin",
      "isOrganizationAssociateAdmin",
      "currentOrganizationType",
      "isOrganizationTeacher",
      "isGeneralAdmin",
      "hasFilter",
    ]),
  },
  methods: {
    ...mapActions({
      deleteReadAloudAction: "readAlouds/deleteReadAloud",
      checkReadAloudPermission: "readAlouds/checkReadAloudPermission",
    }),
    isReadAloudEditable(readAloud) {
      if (readAloud.assigned_group && readAloud.assigned_group.length > 0) {
        return false;
      }
      return true;
    },
    filterShareable(value, row) {
      return row.shareable === value;
    },
    filterCategory(value, row) {
      return row.category === value;
    },
    getRecentAssignGroupName(readAloud) {
      if (readAloud.assigned_group && readAloud.assigned_group.length > 0) {
        let x = readAloud.assigned_group.reduce((prev, current) =>
          new Date(prev.created_at) > new Date(current.created_at) ? prev : current
        );
        return x.group_name;
      }
      return this.$t("common.none");
    },
    getCategoryLabel(v) {
      return getOptionsLabels(DefaultCategoryOptions, v);
    },
    handleDeleteReadAloud(readAloudId) {
      this.modals.showConfirmDelete = true;
      this.deleteReadAloudIds = [readAloudId];
    },
    async handleConfirmDeleteReadAloud() {
      try {
        for (let idx = 0; idx < this.deleteReadAloudIds.length; idx++) {
          await this.deleteReadAloudAction(this.deleteReadAloudIds[idx]);
        }
        successMessage(this.$t("read_aloud.delete_read_aloud_success"))(this);
      } catch (error) {
        return warningMessage(this.$t("read_aloud.delete_read_aloud_warning"))(this);
      } finally {
        this.deleteReadAloudIds = [];
        await this.getReadAloudPage(this.currentPage);
      }
    },
    getShareableLabel(v) {
      return getOptionsLabels(DefaultShareableOptions, v);
    },
    searchReadAloud(search) {
      return this.getReadAloud({ search });
    },
    getReadAloudPage(page) {
      const search = this.queryString;
      return this.getReadAloud({ page, search });
    },
    async getReadAloud({ page = 1, search = null, status = "" }) {
      try {
        this.loading = true;
        let ordering = this.ordering;
        let params = { page, search, status, ordering };
        const { data } = await this.$axios.get(`/v1/schools/reading-alouds/${this.organizationId}/`, { params });
        this.paginateReadAlouds = data;
        if (this.$refs.multipleTable) {
          this.$refs.multipleTable.clearSelection();
        }
        this.currentPage = page;
        this.selectedReadAlouds = [];
        this.actionModal = "";
        await this.handleCheckReadAloudPermission(data);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return date ? format(new Date(date), this.dateFormat) : "--";
    },
    handleEditReadAloud(readAloudId) {
      if (this.canAccessReadAloud(readAloudId, "view")) {
        return warningMessage(this.$t("read_aloud.messages.edit_disabled_message"), "elsa-message info")(this);
      }

      return this.$router.push({
        name: "read-aloud-edit",
        params: {
          readAloudId: readAloudId,
        },
      });
    },
    handleAssignToClassReadAloud(readAloud) {
      this.$refs.addAssignment.handleAssignToClassReadAloud(readAloud);
    },
    async handleReload() {
      await this.getReadAloudPage(this.currentPage);
    },
    handleSelectRow(rowData) {
      this.selectedReadAlouds = rowData;
      if (this.selectedReadAlouds.length === this.paginateReadAlouds.results.length) {
        this.selectText = this.$t("common.deselect_all");
      } else {
        this.selectText = this.$t("common.select_all");
      }
    },
    changeActions(actions) {
      if (actions === "remove") {
        this.modals.showConfirmDelete = true;

        this.deleteReadAloudIds = this.selectedReadAlouds.map(x => x.id);
      }
    },
    selectAll(rows) {
      if (this.selectedReadAlouds.length < this.paginateReadAlouds.results.length) {
        this.$refs.multipleTable.clearSelection();
        rows.forEach(row => {
          this.$refs.multipleTable.toggleRowSelection(row);
        });
        this.selectedReadAlouds = rows;
        this.selectText = this.$t("common.deselect_all");
      } else {
        this.selectText = this.$t("common.select_all");
        this.selectedReadAlouds = [];
        this.$refs.multipleTable.clearSelection();
      }
    },
    async sortChange({ prop, order }) {
      const search = this.queryString;
      if (order == "descending") this.ordering = "-" + prop;
      else this.ordering = prop;
      await this.getReadAloud({ search });
    },
    async handleCheckReadAloudPermission(data) {
      try {
        const readAloudIds = data.results.map(ra => ra.id);
        this.checkedReadAlouds = await this.checkReadAloudPermission(readAloudIds);
      } catch (error) {
        error;
      }
    },
    canAccessReadAloud(readAloudId, action = "edit") {
      return this.checkedReadAlouds[action].includes(Number(readAloudId));
    },
  },
  async mounted() {
    try {
      DefaultCategoryOptions.forEach(option => {
        this.categoryOptions.push({
          text: option.label,
          value: option.value,
        });
      });
      const params = { status: "" };
      await this.getReadAloud(params);
    } catch (error) {
      // console.error(error);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/_colors.scss";

.read-aloud-container {
  width: 100%;
  font-family: "SF Pro Display", sans-serif;

  .read-aloud-tab-body {
    text-align: center;
    padding: 24px 40px 0;
  }

  .create-new-read-aloud {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 16px;
      font-size: 16px;
      max-width: 230px;
      cursor: pointer;
      font-weight: 600;
      font-family: "SF Pro Display";
    }
  }

  .assign-to-group {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 8px 16px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      cursor: pointer;
      font-family: "SF Pro Display";
    }
  }

  .el-table .el-table__row .wrap-avatar small {
    font-size: 16px;
  }

  .elsa-table-layout-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &>div {
      align-self: center;
    }
  }

  .status__label {
    font-size: 16px;
    text-transform: capitalize;
    padding-left: 10px;
  }

  .status__input {
    vertical-align: middle;

    .status__inner {
      border: 1px solid #dcdfe6;
      border-radius: 100%;
      width: 14px;
      height: 14px;
      background-color: #fff;
      cursor: pointer;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      position: relative;
      display: inline-block;
    }
  }

  .status__active .status__inner {
    border-color: #03bd23;
    background-color: #03bd23;
  }

  .status__finished .status__inner {
    border-color: #e6a23c;
    background-color: #e6a23c;
  }

  .status__not_started .status__inner {
    border-color: #909399;
    background-color: #909399;
  }

  .status__pending {
    display: block;
  }

  .status__pending .status__inner {
    border-color: #d9ecff;
    background-color: #d9ecff;
  }

  .wrap-avatar {
    .block {
      word-break: break-word;
    }
  }

  .el-menu.sidemenu {
    .el-menu-item {
      border: none;
      margin-top: 8px;
      margin-bottom: 24px;
      height: 48px !important;

      span {
        text-transform: capitalize;
      }

      svg {
        width: 30px;
      }
    }
  }

  .el-form {
    width: auto;
    padding-bottom: 0px !important;

    &.search-form {
      margin-top: 0px;
      width: 316px;
      margin-bottom: 0px;
      padding-bottom: 0px;
    }
  }

  .el-input {
    border: 1px solid #d8d8d8;
    border-radius: 8px;
  }

  .assign-to-class {
    margin-right: 8px;
    z-index: 1903;

    button {
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      padding: 8px 8px;
      border: 1px solid #007eff;
    }
  }

  .select-all {
    margin-top: 26px;
    margin-left: 16px;
    position: absolute;
  }
}

.remove-read-aloud {
  color: red !important;
}

::v-deep .el-popper {
  margin-left: -75px !important;
  margin-top: 40px !important;
}

::v-deep .el-table__body,
::v-deep .el-table__footer,
::v-deep .el-table__header {
  //table-layout: auto !important;
  width: 100% !important;
}

::v-deep .elsa-table-layout {
  padding-top: 0px;
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 16px;
}

::v-deep .el-table .el-table-column--selection .cell {
  padding-left: 8px;
}

::v-deep .el-table .el-table__header-wrapper .cell {
  font-weight: 600;
  opacity: 1;
  color: #757575;
  padding: 12px 8px;
}

::v-deep .el-table .cell {
  padding: 12px 8px;
}

::v-deep .el-table__header tr {
  background-color: #f9f9f9 !important;
}

::v-deep .search-box.elsa-input {
  margin-top: 16px;
}

::v-deep .el-input .el-input__inner {
  padding: 24px 0px !important;
  opacity: 1 !important;
  font-size: 16px !important;
}

::v-deep .el-checkbox__inner {
  border-color: #828292 !important;
  border-radius: 4px !important;
}

.header-end {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;

  &>div {
    margin: 0px 5px;
  }
}

.more-action-text {
  padding: 24px 0px;
  font-weight: 600;
  margin-right: 8px;
  font-size: 16px;

  a {
    font-size: 16px;
    cursor: pointer;
  }
}

::v-deep .el-input {
  height: 48px !important;
}

::v-deep .el-input-group__append {
  border-radius: 40px !important;
}

::v-deep .el-table__empty-block {
  width: 100% !important;
}

.remove-btn-div {
  display: flex;
  justify-content: flex-end;
}

.remove-btn {
  position: absolute;
  margin-top: 32px;
  margin-right: 48px;
  z-index: 1900;
  color: red;
  padding: 12px 16px;
}

::v-deep .el-table-column--selection .cell {
  width: 3000px;
}

::v-deep .el-table-column--selection .el-checkbox {
  width: 3000px;
  z-index: 1900;
  padding: 24px 8px;
}

::v-deep .el-table .cell {
  padding: 0px 8px;
}

::v-deep .el-table__row {
  td:nth-child(2) {
    z-index: 1901;
  }
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 20px;
  color: $black;
}

::v-deep .el-table__column-filter-trigger .el-icon-arrow-down:before {
  content: "\e790";
}

.btn-edit-disabled {
  color: #c0c4cc !important;
  background-image: none;
  background-color: #fff;
  border-color: #ebeef5;

  svg {
    color: #c0c4cc !important;
  }
}
#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}
</style>
